import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Login = () => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { login, isLoading: authLoading } = useAuth();
	const navigate = useNavigate();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setIsSubmitting(true);

		try {
			await login(email, password);
			navigate("/dashboard");
		} catch (error: any) {
			setError(error.message || "Failed to login");
		} finally {
			setIsSubmitting(false);
		}
	};

	// Determine button loading state using local form submission state to prevent getting stuck
	const isButtonLoading = isSubmitting;

	return (
		<div className='min-h-screen flex items-center justify-center bg-muted/30 px-4'>
			<Card className='w-full max-w-md'>
				<CardHeader className='space-y-1'>
					<img
						src='/logo.png'
						alt='Logo'
						className='h-16 w-16'
					/>
					<CardTitle className='text-2xl font-bold'>Login</CardTitle>
					<CardDescription>
						Enter your credentials to access your account
					</CardDescription>
				</CardHeader>
				<form onSubmit={handleSubmit}>
					<CardContent className='space-y-4'>
						{error && (
							<Alert variant='destructive'>
								<AlertCircle className='h-4 w-4' />
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}

						<div className='space-y-2'>
							<Label htmlFor='email'>Email</Label>
							<Input
								id='email'
								type='email'
								placeholder='name@example.com'
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
								disabled={isButtonLoading}
							/>
						</div>
						<div className='space-y-2'>
							<div className='flex items-center justify-between'>
								<Label htmlFor='password'>Password</Label>
								<Link
									to='/forgot-password'
									className='text-sm text-blue-500 hover:text-blue-700'>
									Forgot password?
								</Link>
							</div>
							<Input
								id='password'
								type='password'
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								disabled={isButtonLoading}
							/>
						</div>
					</CardContent>
					<CardFooter className='flex flex-col space-y-4'>
						<Button
							type='submit'
							className='w-full'
							disabled={isButtonLoading || !email || !password}>
							{isButtonLoading ? "Signing in..." : "Sign in"}
						</Button>
						
            <p className="text-center text-sm text-muted-foreground">
              Made with ❤️ by <a href="https://fra.exondevs.tech" className="text-blue-500 hover:text-blue-700">Francis Tin-ao</a>
            </p>
            
					</CardFooter>
				</form>
			</Card>
		</div>
	);
};

export default Login;
